class ProductTabs extends HTMLElement {
  constructor() {
    super();
    this.tabs = Array.from(this.querySelectorAll('[data-tab-target]'));
    this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));

    // A panel snippet removes itself (see product-*-metaobject.liquid's
    // empty-block fallback) when its own metafield is blank. Prune any tab
    // button left pointing at a panel that's no longer there.
    const panelTargets = this.panels.map((panel) => panel.dataset.tabPanel);
    this.tabs = this.tabs.filter((tab) => {
      const hasPanel = panelTargets.includes(tab.dataset.tabTarget);
      if (!hasPanel) tab.remove();
      return hasPanel;
    });

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(tab.dataset.tabTarget));
    });

    // Forms (like Product Enquiry) redirect back to the page with a
    // #<form-id> anchor and ?contact_posted=true on submit. The target lives
    // inside a panel that's `hidden` unless it's the active tab, so without
    // this the success/error message renders invisibly in a closed tab.
    // Jump to whichever tab actually contains the anchor target first.
    let jumpedToAnchor = false;
    if (window.location.hash) {
      const anchorTarget = document.getElementById(window.location.hash.slice(1));
      const anchorPanel = anchorTarget ? anchorTarget.closest('[data-tab-panel]') : null;
      if (anchorPanel && this.contains(anchorPanel)) {
        this.selectTab(anchorPanel.dataset.tabPanel);
        jumpedToAnchor = true;

        // The fixed header (88px) would otherwise cover the target, and the
        // browser's own native scroll-to-hash can re-snap it flush under the
        // header at various points during load - reapply the header-aware
        // scroll (shared with global.js's enquiry-form fix) a few times over
        // the page's early lifetime so ours is the one that sticks.
        const scrollWithOffset = () => {
          if (typeof scrollToWithHeaderOffset === 'function') {
            scrollToWithHeaderOffset(anchorTarget);
          } else {
            anchorTarget.scrollIntoView({ block: 'center' });
          }
        };
        [50, 300, 800].forEach((delay) => setTimeout(scrollWithOffset, delay));
        window.addEventListener('load', scrollWithOffset);
      }
    }

    // Otherwise, if the tab that was marked active got pruned, fall back to
    // the first remaining one so something is always shown.
    const hasActiveTab = this.tabs.some((tab) => tab.classList.contains('active'));
    if (!jumpedToAnchor && !hasActiveTab && this.tabs.length > 0) {
      this.selectTab(this.tabs[0].dataset.tabTarget);
    }
  }

  selectTab(target) {
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.tabTarget === target;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive);
    });

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.tabPanel === target;
      panel.classList.toggle('active', isActive);
      panel.hidden = !isActive;
    });
  }
}

customElements.define('product-tabs', ProductTabs);
